import {
  DashboardData,
  RestaurantAnalyticsData,
  SalesItem,
  TopDish,
} from "./types";

export const getDishName = (dish: TopDish): string =>
  dish.dishName ?? dish.name ?? "Unknown";

export const getDishQuantity = (dish: TopDish): number =>
  dish.quantitySold ?? dish.quantity ?? 0;

export const getDishRevenue = (dish: TopDish): number =>
  dish.revenue ?? dish.totalRevenue ?? dish.grossSales ?? 0;

export const getRevenueContribution = (dish: TopDish): string => {
  const value = dish.revenueContribution ?? dish.percentage ?? 0;
  return `${Number(value).toFixed(value % 1 === 0 ? 0 : 1)}%`;
};

export const formatCurrency = (value: number): string =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export const formatDateRange = (startDate?: string, endDate?: string): string => {
  if (!startDate || !endDate) return "";
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  const startFormat: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  const endFormat: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };
  return `${start.toLocaleDateString("en-IN", startFormat)} - ${end.toLocaleDateString(
    "en-IN",
    endFormat
  )}`;
};

export const timeAgo = (dateString?: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const getCustomerName = (order: {
  user?: Record<string, any>;
  deliveryAddress?: { fullName?: string };
}): string => {
  const user = order.user || {};
  const full =
    [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
    user.name ||
    user.fullName ||
    order.deliveryAddress?.fullName;
  return full || "Customer";
};

export const getOrderRating = (order: {
  rating?: Array<{ rating?: number; ratingType?: string }>;
}): number => {
  const ratings = order.rating || [];
  const restaurantRating = ratings.find(
    (item) => !item.ratingType || item.ratingType.toLowerCase().includes("restaurant")
  );
  return restaurantRating?.rating ?? ratings[0]?.rating ?? 0;
};

const completedStatuses = new Set([
  "delivered",
  "completed",
  "cancelled",
  "canceled",
]);

export const mapAnalyticsToDashboard = (
  data: RestaurantAnalyticsData
): DashboardData => {
  const summary = data.summary || ({} as RestaurantAnalyticsData["summary"]);
  const orderStatus = data.orderStatus || [];
  const pending =
    orderStatus.length > 0
      ? orderStatus
          .filter((item) => !completedStatuses.has(String(item.status).toLowerCase()))
          .reduce((sum, item) => sum + (item.count || 0), 0)
      : Math.max(
          0,
          (summary.totalOrders || 0) -
            (summary.deliveredOrders || 0) -
            (summary.cancelledOrders || 0)
        );

  const items: SalesItem[] = (data.topDishes || []).map((dish, index) => ({
    id: dish.dishId || String(index),
    dishName: getDishName(dish),
    quantity: getDishQuantity(dish),
    totalAmount: getDishRevenue(dish),
    revenueContribution: dish.revenueContribution ?? dish.percentage,
  }));

  return {
    filter: (data.period?.filter as DashboardData["filter"]) || "today",
    orderStats: {
      newOrders: summary.totalOrders || 0,
      pendingOrders: pending,
      completedOrders: summary.deliveredOrders || 0,
      cancelledOrders: summary.cancelledOrders || 0,
    },
    sales: {
      items,
      totalSalesAmount:
        summary.grossSales ??
        items.reduce((sum, item) => sum + (item.totalAmount || 0), 0),
    },
    analytics: data,
  };
};
